import { Controller } from "@hotwired/stimulus";
import { debugLog } from "../utils";
import { rrFetch } from "../utils/rr_fetch";
import toast from "../utils/toast";

// The withdrawal read from either end: name the payout and the cost follows,
// name the cost and the payout follows. Only the field that is not being typed
// in is replaced, so the caret never moves under the customer.
export default class extends Controller {
  static targets = ["payout", "amount"];
  static values = {
    endpoint: String,
    csrfToken: String,
    payoutMax: Number,
    payoutMin: Number,
    amountMax: Number,
    amountMin: Number,
    cappedMessage: String,
    floorMessage: String,
    debounce: { type: Number, default: 120 },
  };

  connect() {
    debugLog("FxPayoutController: Connected");

    // Submitting takes the fields out of use, and a reprice would hand them
    // back: the stream replaces them with fresh, unfrozen markup. The form's
    // own events say when that window opens and closes.
    this.onSubmitStart = this.seal.bind(this);
    this.onFetchResponse = (e) => {
      if (!e.detail.fetchResponse.response.ok) this.unseal();
    };
    this.onSubmitEnd = (e) => {
      if (e.detail.success === false) this.unseal();
    };

    this.form = this.element.closest("form");
    if (this.form) {
      this.form.addEventListener("turbo:submit-start", this.onSubmitStart);
      this.form.addEventListener("turbo:before-fetch-response", this.onFetchResponse);
      this.form.addEventListener("turbo:submit-end", this.onSubmitEnd);
    }
  }

  disconnect() {
    this.cancel();
    if (this.form) {
      this.form.removeEventListener("turbo:submit-start", this.onSubmitStart);
      this.form.removeEventListener("turbo:before-fetch-response", this.onFetchResponse);
      this.form.removeEventListener("turbo:submit-end", this.onSubmitEnd);
    }
  }

  // Blurring a frozen field fires its own settle, so the pending keystroke and
  // any answer still on its way are dropped along with the new ones.
  seal() {
    this.sealed = true;
    this.cancel();
    this.seq = (this.seq ?? 0) + 1;
  }

  unseal() {
    this.sealed = false;
  }

  repricePayout() {
    if (!this.hasPayoutTarget) return;
    this.reprice(this.payoutTarget, this.payoutMaxValue, "payout", this.hasAmountTarget ? this.amountTarget : null);
  }

  repriceAmount() {
    if (!this.hasAmountTarget) return;
    this.reprice(this.amountTarget, this.amountMaxValue, "amount", this.hasPayoutTarget ? this.payoutTarget : null);
  }

  // Leaving a field settles what it is showing: no half-typed "5." or "5,,",
  // and nothing below the floor, the way every other amount field in the app
  // behaves on blur.
  settlePayout() {
    if (!this.hasPayoutTarget) return;
    this.settle(this.payoutTarget, this.payoutMinValue, formatPayout, "payout");
  }

  settleAmount() {
    if (!this.hasAmountTarget) return;
    this.settle(this.amountTarget, this.amountMinValue, formatAmount, "amount");
  }

  // The whole spendable balance, settled the way leaving the field would settle
  // it, so MAX and a typed maximum reach the same pair of figures.
  useMax() {
    if (this.sealed || !this.hasAmountTarget || !this.amountMaxValue) return;

    this.amountTarget.value = formatAmount(this.amountMaxValue);
    this.amountTarget.focus();
    this.settle(this.amountTarget, this.amountMinValue, formatAmount, "amount");
  }

  settle(field, min, format, name) {
    // readOnly as well as sealed: freezing the field blurs it, and that blur
    // arrives before the submit event this controller seals on.
    if (this.sealed || field.readOnly) return;

    // A keystroke from a moment ago may still be waiting to go out. It would
    // answer after this one and overwrite the settled pair with a loose one.
    this.cancel();

    const value = Number(field.value.replace(/,/g, ""));
    if (!field.value.trim() || Number.isNaN(value) || value <= 0) {
      field.value = "";
      return;
    }

    let settled = value;
    if (min > 0 && settled < min) {
      settled = min;
      toast(this.floorMessageValue || "", "error");
    }

    field.value = format(settled);

    // Settling asks for the pair that actually holds: the cost is rounded up to
    // the cent, so it buys slightly more than was asked for, and this is where
    // the typed figure catches up with what it really buys.
    this.fetchPrice(name, String(settled), true);
  }

  reprice(field, max, name, other) {
    if (this.sealed) return;

    this.clean(field, max);
    this.cancel();

    // The other figure belongs to what was typed a moment ago, and the form
    // requires the dollar one, so clearing it keeps Continue shut until the
    // server answers. Not while the customer is standing in it, though.
    if (other && document.activeElement !== other) other.value = "";

    const value = field.value.replace(/,/g, "").trim();
    this.pending = setTimeout(() => this.fetchPrice(name, value), this.debounceValue);
  }

  // Keeps the field to a grouped number as it is typed. The caret is restored by
  // counting the digits before it, not by offset: separators come and go as the
  // number grows, and an offset drifts by one with every comma inserted.
  clean(field, max) {
    const before = field.value;
    const kept = countKept(before, field.selectionStart);
    const formatted = groupDigits(before);

    const clamped = this.clampTo(formatted, max);
    if (clamped !== null) {
      field.value = clamped;
      this.moveCaretToEnd(field);
      return;
    }

    if (formatted === before) return;

    field.value = formatted;

    try {
      field.setSelectionRange(caretAfter(formatted, kept), caretAfter(formatted, kept));
    } catch {
      // A field without selection support is still correctly formatted.
    }
  }

  // Returns the capped value when one was typed past the ceiling, null
  // otherwise. The warning fires once per overshoot, not once per digit.
  clampTo(formatted, max) {
    if (!max || max <= 0) return null;

    if (Number(formatted.replace(/,/g, "")) <= max) {
      this.warnedAtCap = false;
      return null;
    }

    if (!this.warnedAtCap) {
      this.warnedAtCap = true;
      toast(this.cappedMessageValue || "", "error");
    }
    return groupDigits(String(max));
  }

  moveCaretToEnd(field) {
    try {
      field.setSelectionRange(field.value.length, field.value.length);
    } catch {
      // A field without selection support still carries the capped value.
    }
  }

  async fetchPrice(name, value, settling = false) {
    if (this.sealed || !value || Number(value) <= 0) return;

    // Pricing takes longer than typing, so answers come back out of order. Only
    // the newest one may draw: an older one would overwrite it with a figure
    // for a number the customer has already changed.
    const seq = (this.seq ?? 0) + 1;
    this.seq = seq;

    const body = new FormData();
    body.append(name, value);
    body.append("csrf_token", this.csrfTokenValue);
    if (settling) body.append("settle", "1");

    try {
      // rrFetch, not fetch: without the session and embed headers it carries,
      // the server answers a redirect instead of a stream.
      const response = await rrFetch(this.endpointValue, {
        method: "POST",
        body,
        headers: { Accept: "text/vnd.turbo-stream.html" },
      });
      if (!response.ok) {
        debugLog("FxPayoutController: pricing returned", response.status);
        return;
      }

      const stream = await response.text();
      if (seq !== this.seq) return;
      if (stream && globalThis.Turbo) {
        // Replacing a field destroys the element the caret is in, so where it
        // was ours, it goes back once the new one is in place.
        const focused = this.focusedField();
        const caret = focused ? focused.selectionStart : null;

        await globalThis.Turbo.renderStreamMessage(stream);

        this.restoreFocus(focused === this.payoutTarget ? "payout" : focused ? "amount" : null, caret);
      }
    } catch (error) {
      debugLog("FxPayoutController: pricing failed", error);
    }
  }

  focusedField() {
    const active = document.activeElement;
    if (this.hasPayoutTarget && active === this.payoutTarget) return this.payoutTarget;
    if (this.hasAmountTarget && active === this.amountTarget) return this.amountTarget;
    return null;
  }

  restoreFocus(which, caret) {
    if (!which) return;

    const field = which === "payout" ? (this.hasPayoutTarget ? this.payoutTarget : null) : (this.hasAmountTarget ? this.amountTarget : null);
    if (!field || document.activeElement === field) return;

    field.focus();
    try {
      field.setSelectionRange(caret ?? field.value.length, caret ?? field.value.length);
    } catch {
      // A field without selection support is still focused.
    }
  }

  cancel() {
    if (this.pending) {
      clearTimeout(this.pending);
      this.pending = null;
    }
  }
}

// What a settled figure reads as: dollars always to the cent, a payout to the
// cent only while it is small enough for cents to mean anything.
function formatAmount(value) {
  return groupDigits(value.toFixed(2));
}

function formatPayout(value) {
  return groupDigits(value >= 1000 ? value.toFixed(0) : value.toFixed(2));
}

// One decimal point at most, two places after it, separators before it.
function groupDigits(raw) {
  const cleaned = raw.replace(/[^0-9.]/g, "");
  const [whole, ...rest] = cleaned.split(".");

  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  if (rest.length === 0) {
    return grouped;
  }

  // Truncated, not rounded: rounding changes a digit still being typed.
  return `${grouped}.${rest.join("").slice(0, 2)}`;
}

function countKept(value, offset) {
  return value.slice(0, offset ?? value.length).replace(/[^0-9.]/g, "").length;
}

function caretAfter(value, kept) {
  if (kept <= 0) {
    return 0;
  }

  let seen = 0;
  for (let i = 0; i < value.length; i += 1) {
    if (/[0-9.]/.test(value[i])) {
      seen += 1;
      if (seen === kept) {
        return i + 1;
      }
    }
  }
  return value.length;
}
